import { Sheet } from "plos/src/components/Sheet";
import Subheading from "plos/src/components/Subheading";
import { Button } from "plos/src/components/buttons/Button";
import Link from "plos/src/components/links/Link";
import { Trans, useTranslation } from "react-i18next";
import { useAdobeContext } from "../../contexts/AdobeContext";
import adobePlaceholderDark from "../../img/adobe/adobe-placeholder-dark.png";
import adobePlaceholder from "../../img/adobe/adobe-placeholder.png";
import {
  adobePlaceholderImage,
  buttonGroup,
  contentWrapper,
  descriptionList,
} from "./crestStyles.css";

interface InitialCrestSheet {
  open: boolean;
  handleCreateCrest: (img: Blob, prompt: string) => Promise<void>;
  handleClose: () => void;
  handleOpenNextSheet: () => void;
}

const InitialCrestSheet = ({
  open,
  handleCreateCrest,
  handleClose,
  handleOpenNextSheet,
}: InitialCrestSheet) => {
  const { t } = useTranslation();

  const { embed, enabled } = useAdobeContext();

  const actionButtons = enabled && (
    <div className={buttonGroup}>
      <Button appearance="outlined" fullWidth onPress={handleOpenNextSheet}>
        {t("initialCrestSheet.close", "Close")}
      </Button>
      <Button
        fullWidth
        onPress={() => {
          handleClose();
          embed.createImage({
            badgeHolder: false,
            onImageCreate: async (img, prompt) => {
              await handleCreateCrest(img, prompt);
            },
            onCancel: handleOpenNextSheet,
            onImageCreateError: handleOpenNextSheet,
          });
        }}
      >
        {t("initialCrestSheet.buttonText", "Create My Badge")}
      </Button>
    </div>
  );

  return (
    <Sheet handleClose={handleOpenNextSheet} open={open} footer={actionButtons}>
      <div className={contentWrapper}>
        <picture>
          <source
            srcSet={adobePlaceholderDark}
            media="(prefers-color-scheme: dark)"
          />
          <img
            src={adobePlaceholder}
            className={adobePlaceholderImage}
            alt={t(
              "initialCrestSheet.placeholderImageAlt",
              "Adobe placeholder image",
            )!}
          />
        </picture>
        <Subheading>
          {!enabled
            ? t("initialCrestSheet.subheadingOne", "Coming Soon to Mobile")
            : t(
                "initialCrestSheet.subheadingTwo",
                "Generate your custom team badge",
              )}
        </Subheading>
        <ul className={descriptionList}>
          <Trans ns="common" i18nKey="initialCrestSheet.textOne">
            <li>Fast and easy - Generate a badge in seconds</li>
            <li>Customise badge styles, apply filters and more</li>
            <li>Perfect your design with intuitive editing tools</li>
          </Trans>
        </ul>
        {enabled && (
          <Trans ns="common" i18nKey="initialCrestSheet.textTwo">
            <p>
              Upon submission, your badge will be reviewed to ensure it meets
              our <Link to="/help/terms">terms of use</Link> before it is
              approved for use.
            </p>
          </Trans>
        )}
      </div>
    </Sheet>
  );
};

export default InitialCrestSheet;
